import { Maybe } from "../../types.ts";
import cloneDeep from "../../utilities/cloneDeep.ts";
import {
  ConditionField,
  DimensionDescriptor,
  DimensionField,
  FieldConfiguration,
  FieldConfigurationType,
  ItemDataType,
  MeasureField,
  MetaIcons,
  MetaItemType,
  MetricField,
  ReportField,
} from "../api/biClient.types.ts";
import { getDefaultFormatting } from "./aggregationHelper.ts";

export const newDimensionMeta = (changes: Partial<DimensionDescriptor>) => {
  const meta: DimensionDescriptor = {
    availableFields: [],
    caption: "",
    dictionary: [],
    displayValues: [],
    icon: MetaIcons.Calendar,
    isNew: false,
    keyFieldName: "",
    name: "",
    type: ItemDataType.General,
    itemType: MetaItemType.DIMENSION,
    glAllowed: true,
    ...changes,
  };
  return meta;
};

export const isMeasure = (field: ReportField | undefined | null): field is MeasureField => {
  if (field === undefined || field === null) {
    return false;
  }
  return field.config.type === FieldConfigurationType.Irr || field.config.type === FieldConfigurationType.Measure;
};

export const isMetric = (field: ReportField | undefined | null): field is MetricField => {
  if (field === undefined || field === null) {
    return false;
  }
  return field.config.type === FieldConfigurationType.Metric;
};

export const isIrr = (field: ReportField | undefined | null): field is MeasureField => {
  if (field === undefined || field === null) {
    return false;
  }
  return field.config.type === FieldConfigurationType.Irr;
};

export const isAggregation = (field: ReportField | undefined | null): boolean => {
  return isAggregatedType(field?.config);
};

export const isAggregationFieldConfiguration = (config: Maybe<FieldConfiguration>): boolean => {
  return isAggregatedType(config);
};

export const isAggregatedType = (config: Maybe<Partial<FieldConfiguration>>) => {
  return config?.type === FieldConfigurationType.Aggregation;
};

export const isNotAggregatedType = (config: Maybe<Partial<FieldConfiguration>>) => {
  return config?.type !== FieldConfigurationType.Aggregation || config.aggregation === undefined;
};

export const isAggregationBased = (field: ReportField | undefined | null): field is MeasureField => {
  return isMeasure(field) || isMetric(field) || isAggregation(field);
};

export const isDimension = (field: ReportField | undefined | null): field is DimensionField => {
  return field?.config?.type === FieldConfigurationType.Dimension;
};

export const isDimensionFieldConfiguration = (config: Maybe<FieldConfiguration>) => {
  return config?.type === FieldConfigurationType.Dimension;
};

export const isNotAggregatedDimension = (field: Maybe<ReportField>): field is DimensionField => {
  return isDimension(field) && isNotAggregatedType(field.config);
};

export const isDimensionBased = (field: ReportField | undefined | null): field is DimensionField => {
  if (field === undefined || field === null) {
    return false;
  }
  return isDimensionBasedType(field.config.type);
};

export const isDimensionBasedType = (type: FieldConfigurationType) => {
  return type === FieldConfigurationType.Dimension || type === FieldConfigurationType.Aggregation;
};

export const getMeasures = (fields: ReportField[]): ReportField[] => {
  return fields.filter(isMeasure);
};

export const getDimensions = (fields: ReportField[]): DimensionField[] => {
  return fields.filter(isDimensionBased);
};

export const getAggregationBasedFields = (fields: ReportField[]) => {
  return fields.filter((f) => isAggregationBased(f));
};

export const ensureDefaultFormatIsSet = (field: ReportField): ReportField => {
  if (!field.config.format && isDimensionBased(field)) {
    return {
      ...field,
      config: {
        ...field.config,
        format: getDefaultFormatting(field),
      },
    };
  }

  return field;
};

export const isConditionValid = (field: ConditionField, conditions: ConditionField[]): boolean => {
  if (field.hasLinks) return true;
  const linkedConditions = conditions.filter(
    (v) => v.meta.name === field.meta.name && v.config.guid !== field.config.guid && v.hasLinks
  );
  if (linkedConditions.length > 0) return false;

  const notLinkedConditions = conditions.filter((v) => v.meta.name === field.meta.name && !v.hasLinks);
  if (notLinkedConditions.length === 1) return true;

  return notLinkedConditions.indexOf(field) === 0;
};

export const updateLinkAndValidate = (conditions: ConditionField[], measures: ReportField[]): ConditionField[] => {
  const copy = cloneDeep(conditions);
  copy.forEach((condition) => {
    const index = measures.findIndex(
      (v) =>
        ((isMeasure(v) && v.meta.capabilities.customConditionsAllowed) || isAggregation(v)) &&
        v.config.useCustomConditions &&
        v.config.linkedConditions &&
        v.config.linkedConditions.indexOf(condition.config.guid) > -1
    );
    condition.hasLinks = index > -1;
  });
  copy.forEach((condition) => {
    condition.invalid = !isConditionValid(condition, copy);
  });

  return copy;
};

export const updateTableField = (item: ReportField, changedValues: Partial<ReportField>, fields: ReportField[]) => {
  const field = fields.find((field) => field.config.guid === item.config.guid);
  if (field !== undefined) {
    Object.assign(field, changedValues);
  }
  return fields;
};

export const updateTableFieldsOrder = (fields: ReportField[], guids: string[]) => {
  return guids
    .map((guid) => {
      return fields.find((field) => field.config.guid === guid);
    })
    .filter((field): field is ReportField => !!field);
};
